// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { getLocaleNumberSymbol, NumberSymbol, formatNumber } from '@angular/common';
import { LanguageService } from './language.service';
import { Language } from '../model/enums/language';
import parseDecimalNumber from 'parse-decimal-number';
import { DEFAULT_LANGUAGE } from '../app.constants';

@Injectable({
  providedIn: 'root',
})
export class FormatNumberService {
  private _decimal: string;
  private _group: string;
  private _lang: Language;
  private _minus: string;

  private get _decimalSymbol(): string {
    if (!this._decimal) {
      this._decimal = getLocaleNumberSymbol(this._lang, NumberSymbol.Decimal);
    }
    return this._decimal;
  }

  private get _groupSymbol(): string {
    if (!this._group) {
      this._group = getLocaleNumberSymbol(this._lang, NumberSymbol.Group);
    }
    return this._group;
  }

  private get _minusSymbol(): string {
    if (!this._minus) {
      this._minus = getLocaleNumberSymbol(this._lang, NumberSymbol.MinusSign);
    }
    return this._minus;
  }

  constructor(private languageService: LanguageService) {
    this._lang = Object.values(Language).includes(this.languageService.language)
      ? this.languageService.language
      : DEFAULT_LANGUAGE;
  }

  format(value: string, digitsInfo?: string): string | null {
    const replacedDecimal = this._replaceDecimalSymbol(value);
    return formatNumber(Number(replacedDecimal), this._lang, digitsInfo);
  }

  formatWithBoardLanguage(language: Language, value: number, precision?: number): string {
    const replacedDecimal = this._replaceDecimalSymbol(value);
    return precision !== undefined
      ? formatNumber(Number(replacedDecimal), language, `1.${precision}-${precision}`) // If we get precision we're setting the number of decimals to be exactly that amount of decimals
      : formatNumber(Number(replacedDecimal), language); // Else we don't specify the number of decimals
  }
  /**
   * Formats a number with the given precision, except if the number is less than 1 away from zero, in that case it will return '<1' if the number is positive and '-1' if the number is negative.
   * @param language
   * @param value
   * @param precision
   * @returns
   */
  formatAsCloseToZeroWithBoardLanguage(language: Language, value: number, precision?: number): string {
    return value === 0 || Math.abs(value) >= 1
      ? this.formatWithBoardLanguage(language, value, precision)
      : value > 0
      ? '<1'
      : '-1';
  }

  parse(value: string): string | null {
    const replacedMinusSign = this._minusSymbol !== '-' ? value.replace(this._minusSymbol, '-') : value;
    return parseDecimalNumber(replacedMinusSign, [this._groupSymbol, this._decimalSymbol]).toString();
  }

  private _replaceDecimalSymbol(value: string | number) {
    return this._decimalSymbol !== '.' ? value.toString().replace(this._decimalSymbol, '.') : value;
  }
}
